import React, { useState } from "react";
import { createPortal } from "react-dom";
import { formatNorwegianShort } from "./mySystemsFormRenderers/dateFormat";
import DataService from "../services/data.service";
import MerdeIcon from "./MerdeIcon";

/* ======================
   STATUS → FARGE
   ====================== */

const STATUS_COLOR = {
  AG: "#2ecc71", // grønn
  AY: "#f1c40f", // gul
  AR: "#e74c3c", // rød
};

const getStatusColor = (status) => STATUS_COLOR[status] ?? "#bdc3c7"; // fallback grå

const getMenuPosition = (event, width = 300, height = 280) => {
  const x =
    event.clientX + width > window.innerWidth - 12
      ? event.clientX - width
      : event.clientX;
  const y =
    event.clientY + height > window.innerHeight - 12
      ? event.clientY - height
      : event.clientY;

  return {
    x: Math.max(12, x),
    y: Math.max(12, y),
  };
};

/* ======================
   COMPONENT
   ====================== */

export default function SystemLoggCard({
  title,
  rows,
  userIsModerator,
  onUpdateLoggActive,
  onLoggUpdated,
}) {
  const [menu, setMenu] = useState(null);
  const [editing, setEditing] = useState(false);

  const [commentDraft, setCommentDraft] = useState("");
  const [overrideDraft, setOverrideDraft] = useState("");

  if (!Array.isArray(rows)) return null;

  const closeMenu = () => {
    setMenu(null);
    setEditing(false);
  };

  /* ======================
     ACTIVE
     ====================== */

  const toggleActive = async () => {
    if (!menu) return;

    const row = menu.row;
    const nextActive = !row.active;

    onUpdateLoggActive(row.id, nextActive);
    closeMenu();

    try {
      await DataService.updateLogg({
        id: row.id,
        systemId: row.systemId,
        active: nextActive,
      });
    } catch (error) {
      onUpdateLoggActive(row.id, row.active);
      alert("Kunne ikke oppdatere hendelsen.");
    }
  };

  /* ======================
     COMMENT + OVERRIDE
     ====================== */

  const startEdit = () => {
    if (!menu) return;

    setCommentDraft(menu.row.comment ?? "");
    setOverrideDraft(menu.row.overrideStatus ?? "");
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!menu) return;

    const row = menu.row;
    const prevComment = row.comment;
    const prevOverride = row.overrideStatus;

    const newComment = commentDraft.trim() || null;
    const newOverride = overrideDraft || null;

    // Optimistisk lokalt
    row.comment = newComment;
    row.overrideStatus = newOverride;
    closeMenu();

    try {
      await DataService.updateLogg({
        id: row.id,
        systemId: row.systemId,
        comment: newComment,
        overrideStatus: newOverride,
      });

      // 🔄 Oppdater hele siden (kart + tabeller)
      if (onLoggUpdated) {
        onLoggUpdated();
      }
    } catch (error) {
      row.comment = prevComment;
      row.overrideStatus = prevOverride;
      alert("Kunne ikke oppdatere kommentar / status.");
    }
  };

  return (
    <div
      className="viewcard22"
      onClick={closeMenu}
      style={{ position: "relative" }}
    >
      <h2 style={{ marginBottom: 8 }}>
        {title} ({rows.length})
      </h2>

      {rows.length === 0 ? (
        <div>Ingen (nye) hendelser logget</div>
      ) : (
        <table
          className="systems-table systems-table--logg"
          style={{ tableLayout: "fixed" }}
        >
          <colgroup>
            <col style={{ width: "120px" }} />
            <col style={{ width: "120px" }} />
            <col style={{ width: "50px" }} />
            <col />
            <col style={{ width: "120px" }} />
          </colgroup>

          <thead>
            <tr>
              <th>Opprettet</th>
              <th>Lokasjon</th>
              <th>Merde</th>
              <th>Kommentar</th>
              <th>Oppdatert</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                onContextMenu={(e) => {
                  e.preventDefault();
                  if (!userIsModerator) return;

                  setMenu({
                    ...getMenuPosition(e),
                    row,
                  });
                }}
                className={!row.active ? "row-inactive" : ""}
              >
                <td>{formatNorwegianShort(row.createdAt)}</td>
                <td>{row.cluster ?? "—"}</td>
                <td>{row.nick ?? "—"}</td>

                {/* 🔴 DYNAMISK MERDE-IKON + KOMMENTAR */}
                <td
                  style={{
                    whiteSpace: "normal",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <MerdeIcon status={row.overrideStatus} size={18} />
                  <span style={{ marginLeft: 6 }}>{row.comment ?? "—"}</span>
                </td>

                <td>{formatNorwegianShort(row.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* CONTEXT MENU */}
      {menu && userIsModerator && createPortal(
        <div
          className="context-menu"
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: menu.y,
            left: menu.x,
            zIndex: 1000,
            width: 300,
            maxWidth: "calc(100vw - 24px)",
          }}
        >
          <div className="context-header">
            <div className="context-title">
              (ID {menu.row.id}) {menu.row.nick ?? "—"}{" "}
              {menu.row.cluster ?? "—"}
            </div>
          </div>

          {!editing ? (
            <>
              <div className="context-item" onClick={toggleActive}>
                {menu.row.active ? "Deaktiver hendelse" : "Aktiver hendelse"}
              </div>

              <div className="context-item" onClick={startEdit}>
                Endre kommentar / status
              </div>
            </>
          ) : (
            <div className="context-edit">
              <label style={{ fontSize: 12 }}>Kommentar</label>
              <textarea
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                rows={3}
                style={{ width: "100%", boxSizing: "border-box" }}
                autoFocus
              />

              <label style={{ fontSize: 12, marginTop: 8 }}>
                Override status
              </label>
              <select
                value={overrideDraft}
                onChange={(e) => setOverrideDraft(e.target.value)}
                style={{
                  width: "100%",
                  borderLeft: overrideDraft
                    ? `6px solid ${getStatusColor(overrideDraft)}`
                    : "6px solid transparent",
                }}
              >
                <option value="">⚪ Ingen</option>
                <option value="AG">🟢 Grønn</option>
                <option value="AY">🟡 Gul</option>
                <option value="AR">🔴 Rød</option>
              </select>

              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button className="btn-primary" onClick={saveEdit}>
                  Lagre
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => setEditing(false)}
                >
                  Avbryt
                </button>
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
